import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useBugReportLauncher } from "@/hooks/useBugReportLauncher";
import { BugReportModal } from "@/components/bug-report/BugReportModal";

interface BugReportContextValue {
  launch: () => Promise<void>;
}

const BugReportContext = createContext<BugReportContextValue>({
  launch: async () => {},
});

const CRASH_REPORT_KEY = "podgo_crash_report";

export function BugReportProvider({ children }: { children: ReactNode }) {
  const { open, onOpenChange, preCapture, sourcePageUrl, launch } = useBugReportLauncher();
  const [crashDescription, setCrashDescription] = useState<string | null>(null);

  // On mount, check for crash context left by GlobalErrorBoundary
  useEffect(() => {
    const raw = sessionStorage.getItem(CRASH_REPORT_KEY);
    if (!raw) return;
    // Remove immediately to prevent loops
    sessionStorage.removeItem(CRASH_REPORT_KEY);
    try {
      const data = JSON.parse(raw);
      const lines = ["[Crash Report]"];
      if (data.error) lines.push(`Error: ${data.error}`);
      if (data.url) lines.push(`URL: ${data.url}`);
      if (data.version) lines.push(`Version: ${data.version}`);
      if (data.stack) lines.push("", "Stack trace:", data.stack);
      setCrashDescription(lines.join("\n"));
      launch();
    } catch {
      // Corrupt data — ignore
    }
  }, []);

  const handleOpenChange = (v: boolean) => {
    onOpenChange(v);
    if (!v) {
      setCrashDescription(null);
    }
  };

  return (
    <BugReportContext.Provider value={{ launch }}>
      {children}
      <BugReportModal
        open={open}
        onOpenChange={handleOpenChange}
        preCapture={preCapture}
        sourcePageUrl={sourcePageUrl}
        initialDescription={crashDescription ?? undefined}
        isCrashRecovery={!!crashDescription}
      />
    </BugReportContext.Provider>
  );
}

export function useBugReport() {
  return useContext(BugReportContext);
}
